import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getPricing, getDefaultAddOns } from '../utils/pricing';
import './PackageCustomizer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PackageCustomizer() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOnsAvailable, setAddOnsAvailable] = useState(getDefaultAddOns());

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/pricing?active=true`);
      const basePackages = response.data.filter(p =>
        ['residential', 'commercial'].includes(p.category)
      );
      setPackages(basePackages);

      if (basePackages.length > 0) {
        // Pre-select the popular package
        const popular = basePackages.find(p => p.popular);
        setSelectedBase(popular || basePackages[0]);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }

    // Refresh add-ons from canonical source
    try {
      const pricing = await getPricing();
      if (Array.isArray(pricing?.addOns) && pricing.addOns.length > 0) {
        setAddOnsAvailable(pricing.addOns);
      }
    } catch {
      // Already have fallback
    }
  };

  const toggleAddOn = (addOn) => {
    setSelectedAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id);
      if (exists) {
        return prev.filter(a => a.id !== addOn.id);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const calculateTotal = () => {
    const basePrice = selectedBase?.price || 0;
    const addOnsPrice = selectedAddOns.reduce((sum, addon) => sum + addon.price, 0);
    const subtotal = basePrice + addOnsPrice;
    
    // Calculate tax (Utah state tax ~6.85%)
    const taxRate = 0.0685;
    const tax = subtotal * taxRate;
    
    return {
      subtotal,
      tax,
      total: subtotal + tax
    };
  };

  const handleContinue = () => {
    const { subtotal, tax, total } = calculateTotal();
    
    // Save to localStorage for booking page
    localStorage.setItem('customPackage', JSON.stringify({
      basePackage: selectedBase,
      addOns: selectedAddOns,
      pricing: { subtotal, tax, total }
    }));
    
    // Navigate to booking page
    navigate('/book', { 
      state: { 
        package: selectedBase,
        addOns: selectedAddOns,
        customized: true
      }
    });
  };

  if (loading) {
    return <div className="loading">Loading packages...</div>;
  }

  const { subtotal, tax, total } = calculateTotal();

  return (
    <div className="package-customizer">
      <div className="customizer-header">
        <h1>Build Your Perfect Package</h1>
        <p>Start with a base package, then add optional services</p>
      </div>

      <div className="container">
        <div className="row">
          {/* Left Column - Packages & Add-ons */}
          <div className="col-lg-8">
            {/* Base Package Selection */}
            <section className="customizer-section">
              <h2>1. Choose Your Base Package</h2>
              <div className="base-packages-grid">
                {packages.map(pkg => (
                  <div 
                    key={pkg._id}
                    className={`base-package-card ${selectedBase?._id === pkg._id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
                    onClick={() => setSelectedBase(pkg)}
                  >
                    {pkg.popular && (
                      <div className="popular-badge">MOST POPULAR</div>
                    )}
                    
                    <div className="package-header">
                      <h3>{pkg.name}</h3>
                      <div className="package-price">
                        ${pkg.price}
                      </div>
                    </div>
                    
                    <p className="package-description">{pkg.description}</p>
                    
                    <ul className="package-features">
                      {pkg.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx}>
                          <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {pkg.includedItems && (
                      <div className="package-includes">
                        {pkg.includedItems.photos && (
                          <span className="include-item">{pkg.includedItems.photos} photos</span>
                        )}
                        {pkg.includedItems.aerialShots && (
                          <span className="include-item">{pkg.includedItems.aerialShots} drone shots</span>
                        )}
                      </div>
                    )}

                    <div className="select-indicator">
                      {selectedBase?._id === pkg._id ? (
                        <span className="selected-text">✓ Selected</span>
                      ) : (
                        <span className="select-text">Select</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Add-ons Selection */}
            <section className="customizer-section">
              <h2>2. Add Optional Services</h2>
              <p className="section-subtitle">Enhance your package with these popular add-ons</p>
              
              <div className="add-ons-grid">
                {addOnsAvailable.map(addon => (
                  <div 
                    key={addon.id}
                    className={`addon-card ${selectedAddOns.find(a => a.id === addon.id) ? 'selected' : ''}`}
                    onClick={() => toggleAddOn(addon)}
                  >
                    <div className="addon-icon">{addon.icon}</div>
                    <div className="addon-info">
                      <h4>{addon.name}</h4>
                      <p>{addon.description}</p>
                    </div>
                    <div className="addon-price">
                      +${addon.price}
                    </div>
                    <div className="addon-checkbox">
                      <input 
                        type="checkbox" 
                        checked={!!selectedAddOns.find(a => a.id === addon.id)}
                        onChange={() => toggleAddOn(addon)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Summary */}
          <div className="col-lg-4">
            <div className="summary-card sticky-summary">
              <h3>Your Custom Package</h3>
              
              {selectedBase && (
                <>
                  <div className="summary-item base-package-summary">
                    <div className="summary-item-header">
                      <strong>{selectedBase.name}</strong>
                      <span>${selectedBase.price}</span>
                    </div>
                    <div className="summary-item-details">
                      {selectedBase.includedItems?.photos && (
                        <span>• {selectedBase.includedItems.photos} professional photos</span>
                      )}
                      <span>• {selectedBase.turnaroundTime || '24-48 hour turnaround'}</span>
                    </div>
                  </div>

                  {selectedAddOns.length > 0 && (
                    <div className="summary-addons">
                      <div className="summary-section-title">Add-ons:</div>
                      {selectedAddOns.map(addon => (
                        <div key={addon.id} className="summary-item addon-summary">
                          <div className="summary-item-header">
                            <span>{addon.icon} {addon.name}</span>
                            <span>+${addon.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="summary-divider"></div>

                  <div className="summary-totals">
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Tax (6.85%):</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="summary-row total-row">
                      <strong>Total:</strong>
                      <strong>${total.toFixed(2)}</strong>
                    </div>
                  </div>

                  {selectedAddOns.length > 0 && (
                    <div className="savings-badge">
                      <span className="savings-icon">💰</span>
                      <div>
                        <div className="savings-title">Great Choice!</div>
                        <div className="savings-text">
                          You're getting {selectedAddOns.length} premium add-on{selectedAddOns.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  )}

                  <button 
                    className="btn btn-primary btn-block btn-lg continue-btn"
                    onClick={handleContinue}
                  >
                    Continue to Booking →
                  </button>

                  <div className="trust-indicators">
                    <div className="trust-item">
                      <span className="trust-icon">✓</span>
                      <span>Money-back guarantee</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-icon">✓</span>
                      <span>Fast turnaround</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-icon">✓</span>
                      <span>Professional quality</span>
                    </div>
                  </div>
                </>
              )}

              {!selectedBase && (
                <div className="empty-summary">
                  <p>Select a base package to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA for Mobile */}
      <div className="mobile-cta">
        <div className="mobile-cta-content">
          <div className="mobile-total">
            <span>Total:</span>
            <strong>${total.toFixed(2)}</strong>
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleContinue}
            disabled={!selectedBase}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

export default PackageCustomizer;
